import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { EmpresaService } from 'src/app/services/EmpresaService/empresa.service';
import { IGetEmpresa } from 'src/app/services/EmpresaService/empresa.model';
import {
  IFrotaCarretaItem,
  IFrotaCavaloItem,
  IFrotaMotoristaItem,
  IGetFrotaEmpresaResponse,
  ITransferirProprietarioEmpresaItem,
  ITransferirProprietarioEmpresaResult,
  TipoEntidadeOwner,
  TipoProprietario
} from 'src/app/services/EmpresaService/transferencia-empresa.model';
import { validarPermissao } from 'src/app/core/utils/permissionScreen/permissionScreen';
import Swal from 'sweetalert2';

type SelectOption = { name: string; value: number };

@Component({
  selector: 'app-transferencia-empresa',
  templateUrl: './transferencia-empresa.component.html',
  styleUrl: './transferencia-empresa.component.scss'
})
export class TransferenciaEmpresaComponent implements OnInit {
  isLoading = false;
  podeIncluir = validarPermissao('transferenciaEmpresaFrota', 'incluir');
  tipoProprietarioOptions: SelectOption[] = [
    { name: 'Empresa', value: TipoProprietario.Empresa },
    { name: 'Motorista avulso', value: TipoProprietario.Avulso }
  ];
  empresaOptions: SelectOption[] = [];
  motoristaAvulsoDestinoOptions: SelectOption[] = [];
  cavaloOptions: SelectOption[] = [];
  carretaOptions: SelectOption[] = [];
  motoristaOptions: SelectOption[] = [];
  frota: IGetFrotaEmpresaResponse = {
    Cavalos: [],
    Carretas: [],
    Motoristas: []
  };
  resultado: ITransferirProprietarioEmpresaResult | null = null;

  form = this.fb.group({
    NU_TIPO_ORIG: [TipoProprietario.Empresa as number, Validators.required],
    NU_TIPO_DEST: [TipoProprietario.Empresa as number, Validators.required],
    FK_EPSA_ORIG: [null as number | null],
    FK_EPSA_DEST: [null as number | null],
    FK_MTRT_EXTR_DEST: [null as number | null],
    TX_OBSV: [''],
    cavalos: [[] as number[]],
    carretas: [[] as number[]],
    motoristas: [[] as number[]]
  });

  constructor(
    private fb: FormBuilder,
    private empresaService: EmpresaService
  ) {}

  ngOnInit(): void {
    this.carregarEmpresas();
    this.carregarMotoristasAvulsoDestino();
    this.form.controls.NU_TIPO_ORIG.valueChanges.subscribe(() => {
      this.form.patchValue(
        { FK_EPSA_ORIG: null, cavalos: [], carretas: [], motoristas: [] },
        { emitEvent: false }
      );
      this.limparFrota();
      this.resultado = null;
      if (this.origemEhAvulso) {
        this.carregarFrota();
      }
    });
    this.form.controls.NU_TIPO_DEST.valueChanges.subscribe(() => {
      this.form.patchValue(
        { FK_EPSA_DEST: null, FK_MTRT_EXTR_DEST: null },
        { emitEvent: false }
      );
      this.resultado = null;
    });
    this.form.controls.FK_EPSA_ORIG.valueChanges.subscribe(origem => {
      this.resultado = null;
      this.form.patchValue(
        { cavalos: [], carretas: [], motoristas: [] },
        { emitEvent: false }
      );
      if (this.origemEhAvulso) {
        return;
      }
      if (origem) {
        this.carregarFrota();
      } else {
        this.limparFrota();
      }
    });
  }

  get origemEhAvulso(): boolean {
    return Number(this.form.controls.NU_TIPO_ORIG.value) === TipoProprietario.Avulso;
  }

  get destinoEhAvulso(): boolean {
    return Number(this.form.controls.NU_TIPO_DEST.value) === TipoProprietario.Avulso;
  }

  get origemNome(): string {
    if (this.origemEhAvulso) {
      return 'Motorista avulso';
    }
    return this.nomeEmpresa(this.form.controls.FK_EPSA_ORIG.value);
  }

  get destinoNome(): string {
    if (this.destinoEhAvulso) {
      const avulso = this.nomeAvulso(this.form.controls.FK_MTRT_EXTR_DEST.value);
      return avulso === '—' ? 'Motorista avulso' : avulso;
    }
    return this.nomeEmpresa(this.form.controls.FK_EPSA_DEST.value);
  }

  get totalSelecionados(): number {
    return (
      (this.form.controls.cavalos.value?.length ?? 0) +
      (this.form.controls.carretas.value?.length ?? 0) +
      (this.form.controls.motoristas.value?.length ?? 0)
    );
  }

  get nomesCavalosSelecionados(): string {
    return this.nomesSelecionados(this.form.controls.cavalos.value, this.cavaloOptions);
  }

  get nomesCarretasSelecionadas(): string {
    return this.nomesSelecionados(this.form.controls.carretas.value, this.carretaOptions);
  }

  get nomesMotoristasSelecionados(): string {
    return this.nomesSelecionados(
      this.form.controls.motoristas.value,
      this.motoristaOptions
    );
  }

  get temPlacaSelecionada(): boolean {
    return (
      (this.form.controls.cavalos.value?.length ?? 0) +
        (this.form.controls.carretas.value?.length ?? 0) >
      0
    );
  }

  get origemDestinoIguais(): boolean {
    if (this.origemEhAvulso || this.destinoEhAvulso) {
      return false;
    }
    const origem = this.form.controls.FK_EPSA_ORIG.value;
    const destino = this.form.controls.FK_EPSA_DEST.value;
    return !!origem && !!destino && Number(origem) === Number(destino);
  }

  get podeTransferir(): boolean {
    if (!this.podeIncluir || this.form.invalid || this.totalSelecionados === 0) {
      return false;
    }
    if (!this.origemEhAvulso && !this.form.controls.FK_EPSA_ORIG.value) {
      return false;
    }
    if (!this.destinoEhAvulso && !this.form.controls.FK_EPSA_DEST.value) {
      return false;
    }
    if (this.destinoEhAvulso && this.temPlacaSelecionada && !this.form.controls.FK_MTRT_EXTR_DEST.value) {
      return false;
    }
    return !this.origemDestinoIguais;
  }

  confirmarTransferencia(): void {
    if (!this.podeTransferir) {
      return;
    }

    const itens = this.montarItens();
    const resumo = [
      `<p><strong>De:</strong> ${this.origemNome}</p>`,
      `<p><strong>Para:</strong> ${this.destinoNome}</p>`,
      `<p><strong>Itens:</strong> ${itens.length}</p>`
    ].join('');

    Swal.fire({
      title: 'Confirmar transferência?',
      html: resumo,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Transferir',
      cancelButtonText: 'Cancelar'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }

      this.isLoading = true;
      this.resultado = null;
      this.empresaService
        .TransferirProprietarioEmpresa({
          FK_EPSA_ORIG: this.origemEhAvulso
            ? undefined
            : Number(this.form.controls.FK_EPSA_ORIG.value),
          FK_EPSA_DEST: this.destinoEhAvulso
            ? undefined
            : Number(this.form.controls.FK_EPSA_DEST.value),
          FL_ORIG_AVLS: this.origemEhAvulso,
          FL_DEST_AVLS: this.destinoEhAvulso,
          FK_MTRT_EXTR_DEST: this.destinoEhAvulso
            ? Number(this.form.controls.FK_MTRT_EXTR_DEST.value) || undefined
            : undefined,
          FK_FUNC_OPER: Number(localStorage.getItem('id')) || undefined,
          TX_OBSV: this.form.controls.TX_OBSV.value || undefined,
          Itens: itens
        })
        .subscribe({
          next: response => {
            this.isLoading = false;
            this.resultado = response;
            this.carregarFrota();
            this.carregarMotoristasAvulsoDestino();
            this.form.patchValue({
              cavalos: [],
              carretas: [],
              motoristas: []
            });
            const ok = response.Sucesso?.length ?? 0;
            const bloqueados = response.Bloqueados?.length ?? 0;
            Swal.fire({
              icon: bloqueados && !ok ? 'warning' : 'success',
              title: 'Transferência concluída',
              text: `${ok} item(ns) transferido(s). ${bloqueados} bloqueado(s).`
            });
          },
          error: err => {
            this.isLoading = false;
            Swal.fire({
              icon: 'error',
              title: 'Não foi possível transferir',
              text:
                (typeof err?.error === 'string' && err.error) ||
                err?.error?.Title ||
                err?.message ||
                'Erro inesperado.'
            });
          }
        });
    });
  }

  private carregarEmpresas(): void {
    this.empresaService.GetEmpresaByFilter(undefined, 1, 10000).subscribe({
      next: (response: IGetEmpresa) => {
        this.empresaOptions = (response.ITEMS ?? []).map(item => ({
          name: item.NM_FNTS_EPSA,
          value: item.PK_EPSA
        }));
      }
    });
  }

  private carregarMotoristasAvulsoDestino(): void {
    this.empresaService.GetFrotaEmpresa({ flAvulso: true }).subscribe({
      next: frota => {
        this.motoristaAvulsoDestinoOptions = (frota.Motoristas ?? []).map(
          (item: IFrotaMotoristaItem) => ({
            name: item.TX_CPF_MTRT
              ? `${item.NM_USUA_MTRT} (${item.TX_CPF_MTRT})`
              : item.NM_USUA_MTRT || `Motorista ${item.PK_MTRT}`,
            value: item.PK_MTRT
          })
        );
      }
    });
  }

  private carregarFrota(): void {
    if (!this.origemEhAvulso && !this.form.controls.FK_EPSA_ORIG.value) {
      this.limparFrota();
      return;
    }

    this.isLoading = true;
    this.empresaService
      .GetFrotaEmpresa(
        this.origemEhAvulso
          ? { flAvulso: true }
          : { fkEpsa: Number(this.form.controls.FK_EPSA_ORIG.value) }
      )
      .subscribe({
        next: frota => {
          this.frota = frota;
          this.cavaloOptions = (frota.Cavalos ?? []).map((item: IFrotaCavaloItem) => ({
            name: item.TX_PLCA_CAVA || `Cavalo ${item.PK_CAVA}`,
            value: item.PK_CAVA
          }));
          this.carretaOptions = (frota.Carretas ?? []).map((item: IFrotaCarretaItem) => ({
            name: item.TX_PLCA_CRRT || `Carreta ${item.PK_CRRT}`,
            value: item.PK_CRRT
          }));
          this.motoristaOptions = (frota.Motoristas ?? []).map(
            (item: IFrotaMotoristaItem) => ({
              name: item.TX_CPF_MTRT
                ? `${item.NM_USUA_MTRT} (${item.TX_CPF_MTRT})`
                : item.NM_USUA_MTRT || `Motorista ${item.PK_MTRT}`,
              value: item.PK_MTRT
            })
          );
          this.isLoading = false;
        },
        error: () => {
          this.limparFrota();
          this.isLoading = false;
        }
      });
  }

  private limparFrota(): void {
    this.frota = { Cavalos: [], Carretas: [], Motoristas: [] };
    this.cavaloOptions = [];
    this.carretaOptions = [];
    this.motoristaOptions = [];
  }

  private montarItens(): ITransferirProprietarioEmpresaItem[] {
    const tipoMotorista = this.origemEhAvulso
      ? TipoEntidadeOwner.MotoristaExterno
      : TipoEntidadeOwner.MotoristaEmpresa;
    const cavalos = (this.form.controls.cavalos.value ?? []).map(id => ({
      NU_TIPO_ENTD: TipoEntidadeOwner.Cavalo,
      FK_ENTD: Number(id)
    }));
    const carretas = (this.form.controls.carretas.value ?? []).map(id => ({
      NU_TIPO_ENTD: TipoEntidadeOwner.Carreta,
      FK_ENTD: Number(id)
    }));
    const motoristas = (this.form.controls.motoristas.value ?? []).map(id => ({
      NU_TIPO_ENTD: tipoMotorista,
      FK_ENTD: Number(id)
    }));
    return [...cavalos, ...carretas, ...motoristas];
  }

  private nomeEmpresa(id: number | null | undefined): string {
    if (!id) {
      return '—';
    }
    return (
      this.empresaOptions.find(option => Number(option.value) === Number(id))
        ?.name ?? String(id)
    );
  }

  private nomeAvulso(id: number | null | undefined): string {
    if (!id) {
      return '—';
    }
    return (
      this.motoristaAvulsoDestinoOptions.find(
        option => Number(option.value) === Number(id)
      )?.name ?? String(id)
    );
  }

  private nomesSelecionados(
    ids: number[] | null | undefined,
    options: SelectOption[]
  ): string {
    if (!ids?.length) {
      return '';
    }
    return ids
      .map(
        id =>
          options.find(option => Number(option.value) === Number(id))?.name ??
          String(id)
      )
      .join(', ');
  }
}
